import { FormEvent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import {
    AdminUserEditCompanyOption,
    AdminUserRow,
    AdminUserUpdateResult,
} from '../../services/api';
import { useAdminUserEditOptions, useAdminUserMutations } from '../../hooks/useApi';
import { canUpdateAuth0RootProfileAttributes } from '../../lib/admin-user-auth0-syncability';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { useTouchedFields } from '../../lib/touchedFormValidation';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { dialogFooterClassName } from '../ui/dialog-frame';
import { Field, FormErrorAlert, useFocusFirstInvalid } from '../ui/form-field';
import { useModalDialog } from '../ui/use-modal-dialog';
import {
    formCheckboxListSurfaceClassName,
    formFieldGroupControlClassName,
    formFieldGroupSurfaceClassName,
} from '../ui/formPrimitives';
import { Input } from '../ui/input';
import { ModalOverlay, modalSheetClassName } from '../ui/modal-overlay';
import { dialogTitleClassName } from '../ui/typography';
import { CompanyCombobox } from './CompanyCombobox';
import { formatCompanyLabel, formatTeamLabel } from './companyComboboxLabels';

interface AdminEditUserModalProps {
    open: boolean;
    user: AdminUserRow | null;
    onClose: () => void;
    onSaved?: (result: AdminUserUpdateResult) => void;
}

type EditUserFieldName = 'company' | 'teams';
const EDIT_USER_FIELDS: readonly EditUserFieldName[] = ['company', 'teams'];

function getEditUserFieldErrors(
    companies: AdminUserEditCompanyOption[],
    teamIds: number[],
): Partial<Record<EditUserFieldName, string>> {
    const hasSelectedMembership = companies.some((company) =>
        company.teams.some((team) => teamIds.includes(team.teamId)),
    );
    return {
        company: companies.length === 0 ? 'Choose at least one company.' : undefined,
        teams:
            companies.length > 0 && (teamIds.length === 0 || !hasSelectedMembership)
                ? 'Choose at least one team.'
                : undefined,
    };
}

function userCompaniesToEditable(user: AdminUserRow): AdminUserEditCompanyOption[] {
    if (user.memberships.length === 0) {
        return [];
    }

    const companiesById = new Map<number, AdminUserEditCompanyOption>();
    for (const membership of user.memberships) {
        const existing = companiesById.get(membership.companyId);
        if (existing) {
            existing.teams.push({
                teamId: membership.teamId,
                teamName: membership.teamName,
            });
            continue;
        }
        companiesById.set(membership.companyId, {
            companyId: membership.companyId,
            companyName: membership.companyName,
            teams: [
                {
                    teamId: membership.teamId,
                    teamName: membership.teamName,
                },
            ],
        });
    }

    return [...companiesById.values()];
}

function getSingleMembershipCompanyId(user: AdminUserRow | null): number | null {
    if (!user || user.memberships.length === 0) {
        return null;
    }

    const companyIds = new Set(user.memberships.map((membership) => membership.companyId));
    if (companyIds.size !== 1) {
        return null;
    }

    return [...companyIds][0];
}

export function AdminEditUserModal({ open, user, onClose, onSaved }: AdminEditUserModalProps) {
    const { updateUser } = useAdminUserMutations();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSso, setIsSso] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [companies, setCompanies] = useState<AdminUserEditCompanyOption[]>([]);
    const [teamIds, setTeamIds] = useState<number[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false);
    const pendingMembershipsRef = useRef<{ companyId: number; teamId: number }[]>([]);
    const formRef = useRef<HTMLFormElement>(null);
    const { touchAll, resetTouched, fieldError } = useTouchedFields<EditUserFieldName>();
    const focusFirstInvalid = useFocusFirstInvalid(formRef);
    const currentUserCompanyId = getSingleMembershipCompanyId(user);
    const currentCompanyOptionsQuery = useAdminUserEditOptions(
        currentUserCompanyId == null ? undefined : String(currentUserCompanyId),
        25,
        open && currentUserCompanyId != null,
    );
    const currentCompanyOption =
        currentUserCompanyId == null
            ? null
            : (currentCompanyOptionsQuery.data?.pages
                  .flatMap((page) => page.items)
                  .find((company) => company.companyId === currentUserCompanyId) ?? null);
    const fieldErrors = getEditUserFieldErrors(companies, teamIds);

    useEffect(() => {
        if (!open || !user) return;
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setIsSso(user.isSso);
        setIsActive(user.isActive);
        setCompanies(userCompaniesToEditable(user));
        setTeamIds(user.memberships.map((membership) => membership.teamId));
        setError(null);
        setIsDeactivateConfirmOpen(false);
        resetTouched();
    }, [open, resetTouched, user]);

    useEffect(() => {
        if (!open || !currentCompanyOption) return;

        setCompanies((current) => {
            if (current.length !== 1 || current[0].companyId !== currentCompanyOption.companyId) {
                return current;
            }

            return [currentCompanyOption];
        });
    }, [currentCompanyOption, open]);

    const { titleId, panelProps } = useModalDialog({ open: open && !!user, onClose });
    if (!open || !user) return null;

    const namesManagedByIdentityProvider = !canUpdateAuth0RootProfileAttributes(user.subjectId);

    const toggleTeam = (teamId: number) => {
        setTeamIds((current) =>
            current.includes(teamId)
                ? current.filter((candidate) => candidate !== teamId)
                : [...current, teamId],
        );
    };

    const addOrReplaceCompany = (nextCompany: AdminUserEditCompanyOption) => {
        setCompanies([nextCompany]);
        setTeamIds((current) =>
            current.filter((teamId) => nextCompany.teams.some((team) => team.teamId === teamId)),
        );
        setError(null);
    };

    const saveUser = async (memberships: { companyId: number; teamId: number }[]) => {
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        try {
            const result = await updateUser.mutateAsync({
                userId: user.id,
                request: {
                    firstName: trimmedFirstName,
                    lastName: trimmedLastName,
                    isSso,
                    isActive,
                    memberships,
                },
            });
            onSaved?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not update user. Try again.'));
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(EDIT_USER_FIELDS);

        if (fieldErrors.company || fieldErrors.teams) {
            focusFirstInvalid();
            return;
        }

        const selectedMemberships = companies.flatMap((company) =>
            company.teams
                .filter((team) => teamIds.includes(team.teamId))
                .map((team) => ({
                    companyId: company.companyId,
                    teamId: team.teamId,
                })),
        );
        if (new Set(selectedMemberships.map((membership) => membership.companyId)).size > 1) {
            setError('Users can only belong to one company at a time.');
            return;
        }

        if (user.isActive && !isActive) {
            pendingMembershipsRef.current = selectedMemberships;
            setIsDeactivateConfirmOpen(true);
            return;
        }
        await saveUser(selectedMemberships);
    };

    return (
        <>
            <ModalOverlay>
                <div
                    {...panelProps}
                    className={cn(
                        modalSheetClassName,
                        'flex max-h-[90vh] w-full max-w-3xl flex-col',
                    )}
                >
                    <div className="flex shrink-0 items-start justify-between gap-4 border-b p-5">
                        <div>
                            <h2 id={titleId} className={dialogTitleClassName}>
                                Edit user
                            </h2>
                            <p className="mt-1 text-sm text-neutral-500">
                                Update user attributes and team memberships. Email cannot be
                                changed.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close dialog"
                            className="-m-1 flex size-9 shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        >
                            <X className="size-5" aria-hidden />
                        </button>
                    </div>
                    <form
                        ref={formRef}
                        className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5"
                        onSubmit={handleSubmit}
                    >
                        {error ? <FormErrorAlert message={error} /> : null}

                        <div className="grid gap-4 md:grid-cols-2">
                            <Field
                                label="First name"
                                hint={
                                    namesManagedByIdentityProvider
                                        ? 'Managed by your identity provider'
                                        : undefined
                                }
                            >
                                <Input
                                    value={firstName}
                                    onChange={(event) => setFirstName(event.target.value)}
                                    disabled={namesManagedByIdentityProvider}
                                />
                            </Field>
                            <Field
                                label="Last name"
                                hint={
                                    namesManagedByIdentityProvider
                                        ? 'Managed by your identity provider'
                                        : undefined
                                }
                            >
                                <Input
                                    value={lastName}
                                    onChange={(event) => setLastName(event.target.value)}
                                    disabled={namesManagedByIdentityProvider}
                                />
                            </Field>
                        </div>

                        <Field label="Email">
                            <Input
                                value={user.email}
                                readOnly
                                title={user.email}
                                className="cursor-default bg-neutral-100 text-neutral-600"
                            />
                        </Field>

                        <div className="grid gap-3 md:grid-cols-2">
                            <Toggle
                                label="SSO"
                                checked={isSso}
                                onChange={setIsSso}
                                description="Marks this user as an SSO user in WEVO."
                            />
                            <Toggle
                                label="Active"
                                checked={isActive}
                                onChange={setIsActive}
                                description="Synced users are also blocked/unblocked in Auth0."
                            />
                        </div>

                        <div className="space-y-3">
                            <Field
                                label="Add company"
                                error={fieldError('company', fieldErrors.company)}
                            >
                                <CompanyCombobox
                                    ariaLabel="Add company"
                                    value=""
                                    selectedCompanyId={companies[0]?.companyId ?? null}
                                    onSelect={addOrReplaceCompany}
                                />
                            </Field>
                            <p className="text-xs text-neutral-500">
                                Users can only belong to one company at a time. Choosing a company
                                here replaces the current company selection.
                            </p>

                            <div className="space-y-3">
                                {companies.length === 0 ? (
                                    <div className={formCheckboxListSurfaceClassName}>
                                        <p className="text-sm text-neutral-500">
                                            Choose a company first.
                                        </p>
                                    </div>
                                ) : (
                                    companies.map((company) => (
                                        <Field
                                            key={company.companyId}
                                            as="group"
                                            label={formatCompanyLabel(company)}
                                            error={fieldError('teams', fieldErrors.teams)}
                                        >
                                            <div className={formCheckboxListSurfaceClassName}>
                                                <div className="grid gap-2 md:grid-cols-2">
                                                    {company.teams.map((team) => (
                                                        <label
                                                            key={`${company.companyId}-${team.teamId}`}
                                                            className={cn(
                                                                'flex items-center gap-2 rounded-md p-2 text-sm',
                                                                formFieldGroupControlClassName,
                                                            )}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={teamIds.includes(
                                                                    team.teamId,
                                                                )}
                                                                onChange={() =>
                                                                    toggleTeam(team.teamId)
                                                                }
                                                            />
                                                            <span>{formatTeamLabel(team)}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </Field>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className={dialogFooterClassName}>
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={updateUser.isPending}
                                disabled={updateUser.isPending}
                            >
                                {updateUser.isPending ? 'Saving…' : 'Save changes'}
                            </Button>
                        </div>
                    </form>
                </div>
            </ModalOverlay>
            <ConfirmDialog
                open={isDeactivateConfirmOpen}
                title={`Deactivate ${user.name || user.email}?`}
                description="Synced users are also blocked in Auth0."
                confirmLabel="Deactivate and save"
                pendingLabel="Saving…"
                destructive
                onCancel={() => setIsDeactivateConfirmOpen(false)}
                onConfirm={async () => {
                    await saveUser(pendingMembershipsRef.current);
                    setIsDeactivateConfirmOpen(false);
                }}
            />
        </>
    );
}

function Toggle({
    checked,
    description,
    label,
    onChange,
}: {
    checked: boolean;
    description: string;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-start gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-1 size-4 rounded border-neutral-300"
            />
            <span>
                <span className="block text-sm font-medium text-neutral-950">{label}</span>
                <span className="block text-xs text-neutral-500">{description}</span>
            </span>
        </label>
    );
}
